import { Product, ProductVariant } from "../products/product.types";

export const normalizeWhatsAppNumber = (value?: string): string =>
  value?.replace(/\D/g, "") ?? "";

export const buildWhatsAppUrl = (phone: string | undefined, message?: string): string | undefined => {
  const normalized = normalizeWhatsAppNumber(phone);
  if (!normalized) return undefined;
  const query = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${normalized}${query}`;
};

export interface ServiceActionMessageInput {
  serviceName: string;
  organizationName: string;
  intent?: "inquire" | "reserve" | "request-information" | "request-quote" | "contact";
  language: "es" | "en";
}

export const buildServiceActionMessage = ({
  serviceName,
  organizationName,
  intent = "inquire",
  language,
}: ServiceActionMessageInput): string => {
  if (language === "en") {
    if (intent === "reserve") return `Hello, I would like to reserve the ${serviceName} service from ${organizationName}.`;
    if (intent === "request-quote") return `Hello, I would like to request a quote for the ${serviceName} service from ${organizationName}.`;
    return `Hello, I would like information about the ${serviceName} service from ${organizationName}.`;
  }

  if (intent === "reserve") return `Hola, me gustaría reservar el servicio ${serviceName} de ${organizationName}.`;
  if (intent === "request-quote") return `Hola, me gustaría solicitar una cotización para el servicio ${serviceName} de ${organizationName}.`;
  return `Hola, me gustaría recibir información sobre el servicio ${serviceName} de ${organizationName}.`;
};

export interface ProductOrderMessageInput {
  productName: string;
  organizationName: string;
  quantity?: number;
  presentation?: string;
  price?: string;
  mode?: "interest" | "order";
}

export const buildProductOrderMessage = ({
  productName,
  organizationName,
  quantity,
  presentation,
  price,
  mode = "order",
}: ProductOrderMessageInput): string => {
  if (mode === "interest") {
    const detail = presentation ? ` en presentación ${presentation}` : "";
    return `Hola, me interesa ${productName}${detail} de ${organizationName}.`;
  }

  if (quantity === undefined) {
    const presentationDetail = presentation ? `, presentación ${presentation}` : "";
    const priceDetail = price ? `, precio ${price}` : "";
    return `Hola, me interesa ${productName}${presentationDetail}${priceDetail} de ${organizationName}. Quisiera consultar disponibilidad y realizar un pedido.`;
  }

  const detail = presentation ? ` (${presentation})` : "";
  return `Hola, quisiera pedir ${quantity} x ${productName}${detail} de ${organizationName}.`;
};

export const resolveProductPrice = (product: Product): string | undefined =>
  product.variants?.[0]?.price?.label?.es ?? product.price?.label?.es;

export const resolveProductVariants = (product: Product): readonly ProductVariant[] =>
  product.variants ?? [];

const stableHash = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

export const selectDeterministicItem = <T>(
  items: readonly T[],
  stableKey: string
): T | undefined => {
  if (!items.length) return undefined;
  return items[stableHash(stableKey) % items.length];
};

export const selectDeterministicSample = <T>(
  items: readonly T[],
  limit: number,
  stableKey: string
): T[] => {
  if (!items.length || limit <= 0) return [];
  const start = stableHash(stableKey) % items.length;
  return Array.from({ length: Math.min(limit, items.length) }, (_, index) =>
    items[(start + index) % items.length]
  );
};
